import { falArrowDownWideShort } from 'shared/@generated/icons/falArrowDownWideShort';
import { useState } from 'react';
import { Platform, View } from 'react-native';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import { Product, ProductCategory } from 'shared/@generated/models';
import { Paginator, Props } from 'shared/@types';
import { Breadcrumb } from 'shared/components/breadcrumb';
import { ShopProductCard } from 'shared/components/shop/product-card';
import { H1 } from 'shared/components/title';
import { sanitizeQueryParams, prepareDataForApi } from 'shared/library/api-client';
import { PageBuilder } from 'shared/library/page-builder';
import { useDebouncedQueryFilter } from 'shared/library/use-debounced-query-filter';
import { useRoute } from 'shared/library/routes/route';
import { apiRoute } from 'shared/library/api-route';
import { Pagination } from 'shared/components/pagination';
import {
  defaultShopCategoryFilter,
  ShopCategoryFilter,
  ShopCategoryFilterProvider,
  sortOptions,
  useShopCategoryFilterStore,
} from 'shared/state/shop-category-filter';
import { DropdownSelect } from 'shared/components/form/dropdown-select';

import { Text } from 'shared/components/text';

export interface ShopCategoryScreenProps extends Props {
  productCategory: ProductCategory;
  initialFilter: ShopCategoryFilter;
  products: Paginator<Product>;
}

function ShopCategoryScreen(props: ShopCategoryScreenProps) {
  return (
    <ShopCategoryFilterProvider key={props.productCategory.id} initialState={props.initialFilter}>
      <ScreenContent {...props} />
    </ShopCategoryFilterProvider>
  );
}

function ScreenContent({ productCategory, initialFilter, products }: ShopCategoryScreenProps) {
  const { t } = useTranslation(['screens', 'components']);
  const route = useRoute();
  const setFilter = useShopCategoryFilterStore((state) => state.setFilter);
  const filter = useShopCategoryFilterStore((state) => state.filter);
  const [currentProducts, setCurrentProducts] = useState(products);

  useDebouncedQueryFilter(initialFilter, filter, () => {
    setFilter(initialFilter);
    setCurrentProducts(products);
  });

  return (
    <View className={clsx('p-3 md:p-6 md:py-8', Platform.OS !== 'web' && 'min-h-0 flex-1')}>
      <View
        className={clsx(
          'mx-auto w-full max-w-screen-xl',
          Platform.OS !== 'web' && 'min-h-0 flex-1',
        )}
      >
        <Pagination
          paginator={currentProducts}
          onPageChange={(page) => setFilter({ page })}
          renderItem={(item) => <ShopProductCard key={item.id} product={item} />}
          ListHeader={() => (
            <>
              {Platform.OS === 'web' && (
                <Breadcrumb
                  className={'mb-1.5'}
                  items={[
                    { text: t('shop.shop_home.title'), href: route('shop.home') },
                    { text: productCategory.name },
                  ]}
                />
              )}

              <H1
                title={productCategory.name}
                subtitle={productCategory.summary}
                hideTitleWhenNativeHeader={true}
              />

              {Platform.OS === 'web' && (
                <View className={'mt-3 flex-row items-center gap-2 pb-2 md:gap-3 md:py-2'}>
                  <Text
                    className={
                      'text-brand-brown-dark min-w-0 flex-1 text-sm font-semibold md:text-base'
                    }
                    accessibilityLiveRegion={'polite'}
                    numberOfLines={1}
                  >
                    {t('shop.shop_category.matches_found', { count: currentProducts.total })}
                  </Text>

                  <DropdownSelect
                    title={t('components:listings_filter_form.sort.recommended')}
                    options={[
                      {
                        label: t('components:listings_filter_form.sort.recommended'),
                        value: null,
                      },
                      ...sortOptions
                        .filter((value) => value !== 'recommended')
                        .map((value) => ({
                          label: t(`components:listings_filter_form.sort.${value}`),
                          value,
                        })),
                    ]}
                    value={filter.sortBy}
                    dense={true}
                    bold={true}
                    contentAlign={'end'}
                    triggerClassName={'flex-none'}
                    triggerIcon={falArrowDownWideShort}
                    onChange={(sortBy) => setFilter({ sortBy })}
                  />
                </View>
              )}
            </>
          )}
          cols={2}
          lgCols={4}
          xlCols={5}
        />
      </View>
    </View>
  );
}

ShopCategoryScreen.pageBuilder = new PageBuilder<
  ShopCategoryScreenProps,
  { category: string } & ShopCategoryFilter
>()
  .includeShopData()
  .request(({ req, params }) =>
    req<{ product_category: ProductCategory }>(
      apiRoute('product-categories.show', { product_category: params.category }),
    ).then((response) => ({
      productCategory: response.product_category,
    })),
  )
  .request(({ req, params }) => {
    return req<{ products: Paginator<Product> }>(
      apiRoute('products.index'),
      prepareDataForApi(params),
    ).then((response) => ({
      products: response.products,
      initialFilter: sanitizeQueryParams(
        {
          page: params.page ?? defaultShopCategoryFilter.page,
          sortBy: params.sortBy ?? defaultShopCategoryFilter.sortBy,
        },
        defaultShopCategoryFilter,
      ),
    }));
  });

export { ShopCategoryScreen };
